import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { ChevronDown, Send, X } from "lucide-react";
import {
    addPONoteRequest,
    deletePONoteRequest,
    getPONotesRequest,
    PurchaseOrdersLoadingKeys,
} from "../../slices/purchasesOrdersSlice";

function formatNoteTimestamp(value) {
    if (!value) return "";
    try {
        return format(new Date(value), "MMM d 'at' h:mm a");
    } catch {
        return "";
    }
}

export const POSidebarNotes = ({ poId, access }) => {
    const dispatch = useDispatch();
    const { poNotes, loadingKeys } = useSelector((state) => state.purchasesOrders);
    const [quickNoteInput, setQuickNoteInput] = useState("");
    const [notesExpanded, setNotesExpanded] = useState(true);

    const isLoadingNotes = loadingKeys.includes(PurchaseOrdersLoadingKeys.poNotes);
    const isAddingNote = loadingKeys.includes(PurchaseOrdersLoadingKeys.addPONote);
    const canAddNotes = access === "edit" && Boolean(poId);

    useEffect(() => {
        if (poId) {
            dispatch(getPONotesRequest({ poId }));
            setQuickNoteInput("");
            setNotesExpanded(true);
        }
    }, [poId, dispatch]);

    const appendQuickNote = async () => {
        const trimmed = quickNoteInput.trim();
        if (!trimmed || !canAddNotes || isAddingNote) return;
        try {
            await dispatch(addPONoteRequest({ poId, note: trimmed })).unwrap();
            setQuickNoteInput("");
        } catch {
            // toast handled in slice
        }
    };

    const removeNote = (entry) => {
        if (!canAddNotes) return;
        const confirmed = window.confirm("Remove this note from the PO's Notes? The Change Log is not affected.");
        if (!confirmed) return;
        dispatch(deletePONoteRequest({ noteId: entry.id }));
    };

    const hasNotes = poNotes.length > 0;

    return (
        <div className="mt-6">
            <h3 className="text-base font-bold mb-2">Notes</h3>
            <div className="space-y-2">
                {hasNotes && (
                    <button
                        type="button"
                        onClick={() => setNotesExpanded((current) => !current)}
                        className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                    >
                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform duration-150 ${notesExpanded ? "" : "-rotate-90"}`}
                        />
                        {notesExpanded ? "Hide notes" : "Show notes"}
                    </button>
                )}
                {notesExpanded && (
                    <div className="space-y-2 overflow-y-auto max-h-[240px]">
                        {isLoadingNotes ? (
                            <p className="text-[12px] text-gray-500 italic py-3 text-center">
                                Loading notes...
                            </p>
                        ) : hasNotes ? (
                            poNotes.map((entry) => (
                                    <div
                                        key={entry.id}
                                        className="group relative rounded-md bg-gray-50 border border-gray-200 px-3 py-2"
                                    >
                                        {canAddNotes && (
                                            <button
                                                type="button"
                                                onClick={() => removeNote(entry)}
                                                className="absolute top-1 right-1 p-0.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Remove note"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        <div className="flex items-center justify-between gap-2 mb-0.5">
                                            <span className="text-[11px] font-semibold text-gray-900">
                                                {entry.authorName || "Kings Loot Team"}
                                                {entry.source === "EMAIL" && (
                                                    <span className="ml-1.5 px-1 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium">
                                                        Email
                                                    </span>
                                                )}
                                                {entry.source === "BILLCOM_AP_WRITEBACK" && (
                                                    <span className="ml-1.5 px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">
                                                        Bill.com
                                                    </span>
                                                )}
                                            </span>
                                            <span className={`text-[10px] text-gray-500 ${canAddNotes ? "group-hover:pr-4" : ""}`}>
                                                {formatNoteTimestamp(entry.createdAt || entry.created_at)}
                                            </span>
                                        </div>
                                        <p className="text-[12px] text-gray-900 leading-relaxed whitespace-pre-wrap">
                                            {entry.note}
                                        </p>
                                    </div>
                            ))
                        ) : (
                            <p className="text-[12px] text-gray-500 italic py-3 text-center">
                                No notes yet. Add a quick note below.
                            </p>
                        )}
                    </div>
                )}
                {canAddNotes && (
                    <div className="flex items-end gap-2">
                        <textarea
                            value={quickNoteInput}
                            onChange={(e) => setQuickNoteInput(e.target.value)}
                            placeholder="Add a note..."
                            rows={1}
                            disabled={isAddingNote}
                            className="flex-1 px-3 py-2 text-[13px] border border-gray-300 rounded-md bg-white resize-none placeholder:text-gray-400 min-h-[36px] max-h-[100px] disabled:opacity-50"
                            onKeyDown={(e) => {
                                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                    e.preventDefault();
                                    appendQuickNote();
                                }
                            }}
                        />
                        <button
                            type="button"
                            disabled={!quickNoteInput.trim() || isAddingNote}
                            onClick={appendQuickNote}
                            className="p-3 flex items-center justify-center rounded-md bg-black text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                            title="Send (Cmd+Enter)"
                        >
                            <Send className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
